import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useKoddiTheme } from 'koddi-components/ThemeProvider';
import {
    TooltipProps,
    IconTooltipWrapperProps,
    IconTooltipProps,
} from './Tooltip.types';
import {
    TooltipContainer,
    TippyTooltip,
    TooltipIcon,
    IconTooltipWrapperBase,
} from './Tooltip.styled';

/**
 * The Koddi `Tooltip` component is an accessible and localized
 * tooltip for display helpful information in the Koddi platform.
 */
const Tooltip = ({
    translationKey,
    defaultText,
    variant = 'default',
    placement,
    children,
    delay = 0,
    content,
    hideToolTip,
    ...rest
}: TooltipProps): JSX.Element => {
    const { t } = useTranslation();
    const { tooltip, zIndex } = useKoddiTheme();
    const title = t(translationKey, defaultText);

    if (hideToolTip) {
        return <>{children}</>;
    }

    return (
        <TippyTooltip
            placement={placement || 'right'}
            content={content || title}
            className={`koddi-tooltip koddi-tooltip-${variant}`}
            arrow={variant === 'fancy'}
            maxWidth={tooltip.maxWidth}
            zIndex={zIndex.popup}
            animation="fade"
            delay={delay}
            {...rest}
        >
            {children}
        </TippyTooltip>
    );
};

/**
 * A koddi `Icon` with a tooltip.
 */
export const IconTooltip = ({
    icon,
    width,
    height,
    onClick,
    isPrimaryColor,
    margin,
    isDisabled,
    color,
    ...rest
}: IconTooltipProps): JSX.Element => {
    return (
        <Tooltip {...rest}>
            <TooltipContainer onClick={onClick} tabIndex={0} margin={margin}>
                <TooltipIcon
                    color={color}
                    isDisabled={isDisabled}
                    icon={icon}
                    width={width}
                    height={height}
                    isPrimaryColor={isPrimaryColor}
                />
            </TooltipContainer>
        </Tooltip>
    );
};

/**
 * A wrapper for creating an element that has an `IconTooltip`
 * to the right that is center aligned.
 */
export const IconTooltipWrapper: FunctionComponent<IconTooltipWrapperProps> = ({
    children,
    tooltipProps,
    icon,
    translationKey,
    defaultText,
    iconHeight,
    iconWidth,
    isPrimaryColor,
    margin,
    ...rest
}) => {
    return (
        <IconTooltipWrapperBase alignItems="center" {...rest}>
            {children}
            <IconTooltip
                {...tooltipProps}
                translationKey={translationKey}
                defaultText={defaultText}
                icon={icon}
                width={iconWidth}
                height={iconHeight}
                isPrimaryColor={isPrimaryColor}
                margin={margin}
            />
        </IconTooltipWrapperBase>
    );
};

export default Tooltip;
